import { Component, Inject, Prop, Provide, Vue, Watch } from 'vue-property-decorator';
import { AlertService } from '@gv/ammo-grome';
import { AlertType, FunctionAuthorization, GardianService, IFunctionAuthorization, QueryService } from '@gv/ammo-astra';
import BusinessAnalyticsService from '@/core/spe/dashboard/business-analytics.service';
import BackendI18nService from '@/shared/config/spe/backend-i18n.service';
import VueApexCharts from 'vue-apexcharts';
import { colors } from 'vuetify/lib';
import Formatter from '@/move-into-ammo/fromatter';
import ParameterService from '@/shared/config/spe/parameter.service';
import ParameterGroup from '@/core/spe/parameter/parameter-group.vue';
import { IParameters, Parameters } from '@/shared/config/spe/parameters';
import KpiCard from '@/core/spe/dashboard/components/kpi-card.vue';

const ERP_FUNCTION = 'CONSCSP';

@Component({
  components: {
    VueApexCharts,
    ParameterGroup,
    KpiCard,
  },
})
export default class SalesOrdersDashboard extends Vue {
  @Inject('alertService')
  private alertService: () => AlertService;

  @Inject('queryService')
  private queryService: () => QueryService;

  @Inject('gardianService')
  private gardianService: () => GardianService;

  @Inject('businessAnalyticsService')
  private businessAnalyticsService: () => BusinessAnalyticsService;

  @Inject('backendI18nService')
  private backendI18nService: () => BackendI18nService;

  @Inject('parameterService')
  private parameterService: () => ParameterService;

  @Prop({ required: true, type: Number }) readonly carouselItemIndex;
  @Prop({ required: true, type: Number }) readonly activeCarouselItemIndex;

  public slide: { [k: string]: any } = {
    name: '', // Will be set in created()
    color: '#df1e5a',
    data: {
      currencyCode: '',
      netPriceExcludeTax: 0,
      number: 0,
    },
  };
  public formatter = null;
  public parameters: { [k: string]: any } = {};
  public queryParameters: { [k: string]: any } = null;
  public parametersReady = false;
  public authorized = true;
  public isParameterDrawerActive = true;

  // --- Function authorization ---
  public functionAuthorization: IFunctionAuthorization = new FunctionAuthorization(null, false, false, false, null, [], [], []);
  public isFunctionAuthorizationFetching = false;
  public parameter: IParameters = new Parameters(null, null, null, null, null);

  public salesOrderTotalsByReps: { [k: string]: any } = {
    loading: false,
    chartWidth: '100%',
    options: {},
    series: [],
    originalData: null, // Store original response with i18n keys
    chartKey: 0, // Force re-render when data changes
  };

  public salesOrderTotalsByCreateUser: { [k: string]: any } = {
    loading: false,
    chartWidth: '100%',
    options: {},
    series: [],
    originalData: null, // Store original response with i18n keys
    chartKey: 0, // Force re-render when data changes
  };

  public salesOrderTotalsByWeek: { [k: string]: any } = {
    loading: false,
    chartWidth: '100%',
    options: {},
    series: [],
    originalData: null, // Store original response with i18n keys
    chartKey: 0, // Force re-render when data changes
  };

  public salesOrderTotalsByGroup: { [k: string]: any } = {
    loading: false,
    chartWidth: '100%',
    options: {},
    series: [],
    originalData: null, // Store original response with i18n keys
    chartKey: 0, // Force re-render when data changes
  };

  // KPI 1 - Monthly Order Value (independent)
  public currentMonthKPI1: Date = new Date();
  public monthlyOrderValueData: any[] = [];
  public loadingKPI1 = false;

  // KPI 2 - Monthly Number of Orders (independent)
  public currentMonthKPI2: Date = new Date();
  public monthlyOrderCountData: any[] = [];
  public loadingKPI2 = false;

  // KPI 3 - Yearly Total Value (independent)
  public currentYearKPI3: Date = new Date();
  public yearlyTotalData: any[] = [];
  public loadingKPI3 = false;

  // KPI 4 - Yearly Fulfillment Time (independent)
  public currentYearKPI4: Date = new Date();
  public yearlyFulfillmentData: any[] = [];
  public loadingKPI4 = false;

  get disabled(): boolean {
    return this.carouselItemIndex !== this.activeCarouselItemIndex;
  }

  created() {
    // Set i18n title
    this.slide.name = this.$t('rafaleApp.spe.dashboard.salesOrders.title') as string;

    this.formatter = new Formatter(this.$vuetify.lang.current, this.$vuetify.theme);
    this.retrieveFunctionAuthorization()
      .then(() => {
        const parameters = this.parameterService().fromFunctionAuthorization(this.functionAuthorization);
        this.parameterService().hydrateParameters(this, parameters);
        this.parametersReady = true;
      })
      .catch(err => {
        this.parametersReady = false;
      });
  }

  mounted() {
    this.fetch();
  }

  public beforeDestroy() {
    this.isParameterDrawerActive = false;
  }

  @Watch('activeCarouselItemIndex')
  onActiveCarouselItemIndexChange(val: number) {
    if (val === this.carouselItemIndex) {
      this.fetch();
    }
  }

  @Watch('queryParameters')
  function(newQueryParameters) {
    this.fetch();
  }

  @Watch('$store.state.translationStore.currentLanguage')
  onLanguageChange(newLanguage: string, oldLanguage: string) {
    if (newLanguage !== oldLanguage) {
      // Re-translate existing data instead of re-fetching
      this.retranslateCharts();
    }
  }

  retranslateCharts() {
    console.log('Retranslating charts for language:', this.$i18n.locale);
    // Re-translate all charts that have original data
    if (this.salesOrderTotalsByReps.originalData) {
      this.applyTranslation(this.salesOrderTotalsByReps.originalData, this.salesOrderTotalsByReps);
    }
    if (this.salesOrderTotalsByCreateUser.originalData) {
      this.applyTranslation(this.salesOrderTotalsByCreateUser.originalData, this.salesOrderTotalsByCreateUser);
    }
    if (this.salesOrderTotalsByWeek.originalData) {
      this.applyTranslation(this.salesOrderTotalsByWeek.originalData, this.salesOrderTotalsByWeek);
    }
    if (this.salesOrderTotalsByGroup.originalData) {
      this.applyTranslation(this.salesOrderTotalsByGroup.originalData, this.salesOrderTotalsByGroup);
    }
  }

  applyTranslation(originalData: any, chart: any) {
    // Translate i18n keys from backend response
    const translatedRes = this.backendI18nService().translateChartData(JSON.parse(JSON.stringify(originalData)));

    console.log('Original title:', originalData.options?.title?.text);
    console.log('Translated title:', translatedRes.options?.title?.text);
    console.log('Original subtitle:', originalData.options?.subtitle?.text);
    console.log('Translated subtitle:', translatedRes.options?.subtitle?.text);

    // Use Vue.set to ensure reactivity
    this.$set(chart, 'options', translatedRes.options);
    this.$set(chart, 'series', translatedRes.series ?? translatedRes.doubleSeries);

    // Hide ApexCharts built-in title and subtitle (we use v-card-title/subtitle instead for Vuetify consistency)
    // Keep the text for v-card-title/subtitle to access, but don't display it in the chart
    if (chart.options.title) {
      chart.options.title.style = {
        fontSize: '0px',
        color: 'transparent',
      };
      chart.options.title.margin = 0;
    }

    if (chart.options.subtitle) {
      chart.options.subtitle.style = {
        fontSize: '0px',
        color: 'transparent',
      };
      chart.options.subtitle.margin = 0;
    }

    // Colors are now set globally in main.ts via window.Apex.colors

    if (chart.options.chart.id === 'sales-orders-lines-totals-by-week') {
      chart.options.xaxis.max = chart.options.xaxis.categories.length;
      chart.options.colors = '#f37422'; // Override for this specific chart
    }

    this.evalFormatter(chart);
  }

  getApexChart(url: string, params: { [k: string]: any }, chart) {
    chart.loading = true;

    this.businessAnalyticsService()
      .retrieve(url, params)
      .then(res => {
        // Store original response with i18n keys
        chart.originalData = res;

        // Apply translation
        this.applyTranslation(res, chart);

        // Increment chartKey to force re-render
        chart.chartKey = (chart.chartKey || 0) + 1;
      })
      .catch(err => {
        chart.series = [];
        this.alertService().showHttpError(this, err.response);
      })
      .finally(() => (chart.loading = false));
  }

  /**
   * Helper to simplify setting formatter in apex charts.
   * @param chart
   */
  evalFormatter(chart) {
    if (chart.options.yaxis && chart.options.yaxis.labels.formatter) {
      chart.options.yaxis.labels.formatter = eval(chart.options.yaxis.labels.formatter);
    }

    if (chart.options.dataLabels && chart.options.dataLabels.formatter) {
      chart.options.dataLabels.formatter = eval(chart.options.dataLabels.formatter);
    }
  }

  // ========== KPI 1 Navigation ==========
  previousMonthKPI1() {
    this.currentMonthKPI1 = new Date(this.currentMonthKPI1.getFullYear(), this.currentMonthKPI1.getMonth() - 1, 1);
    this.getMonthlyOrderValueData();
  }

  nextMonthKPI1() {
    this.currentMonthKPI1 = new Date(this.currentMonthKPI1.getFullYear(), this.currentMonthKPI1.getMonth() + 1, 1);
    this.getMonthlyOrderValueData();
  }

  // ========== KPI 2 Navigation ==========
  previousMonthKPI2() {
    this.currentMonthKPI2 = new Date(this.currentMonthKPI2.getFullYear(), this.currentMonthKPI2.getMonth() - 1, 1);
    this.getMonthlyOrderCountData();
  }

  nextMonthKPI2() {
    this.currentMonthKPI2 = new Date(this.currentMonthKPI2.getFullYear(), this.currentMonthKPI2.getMonth() + 1, 1);
    this.getMonthlyOrderCountData();
  }

  // ========== KPI 3 Navigation ==========
  previousYearKPI3() {
    this.currentYearKPI3 = new Date(this.currentYearKPI3.getFullYear() - 1, 0, 1);
    this.getYearlyTotalData();
  }

  nextYearKPI3() {
    this.currentYearKPI3 = new Date(this.currentYearKPI3.getFullYear() + 1, 0, 1);
    this.getYearlyTotalData();
  }

  // ========== KPI 4 Navigation ==========
  previousYearKPI4() {
    this.currentYearKPI4 = new Date(this.currentYearKPI4.getFullYear() - 1, 0, 1);
    this.getYearlyFulfillmentData();
  }

  nextYearKPI4() {
    this.currentYearKPI4 = new Date(this.currentYearKPI4.getFullYear() + 1, 0, 1);
    this.getYearlyFulfillmentData();
  }

  /**
   * Fetch monthly order VALUE data for KPI 1
   */
  getMonthlyOrderValueData() {
    const params = {
      ...this.queryParameters,
      month: this.currentMonthKPI1.getMonth() + 1,
      year: this.currentMonthKPI1.getFullYear(),
    };

    this.loadingKPI1 = true;
    this.businessAnalyticsService()
      .retrieve('v1/business-analytics/sales/order/value-by-month-and-year', params)
      .then(res => {
        this.monthlyOrderValueData = res;
      })
      .catch(err => {
        this.monthlyOrderValueData = [];
        this.alertService().showHttpError(this, err.response);
      })
      .finally(() => {
        this.loadingKPI1 = false;
      });
  }

  /**
   * Fetch monthly order COUNT data for KPI 2
   */
  getMonthlyOrderCountData() {
    const params = {
      ...this.queryParameters,
      month: this.currentMonthKPI2.getMonth() + 1,
      year: this.currentMonthKPI2.getFullYear(),
    };

    this.loadingKPI2 = true;
    this.businessAnalyticsService()
      .retrieve('v1/business-analytics/sales/order/value-by-month-and-year', params)
      .then(res => {
        this.monthlyOrderCountData = res;
      })
      .catch(err => {
        this.monthlyOrderCountData = [];
        this.alertService().showHttpError(this, err.response);
      })
      .finally(() => {
        this.loadingKPI2 = false;
      });
  }

  /**
   * Fetch yearly total data for KPI 3
   */
  getYearlyTotalData() {
    const params = {
      ...this.queryParameters,
      year: this.currentYearKPI3.getFullYear(),
    };

    this.loadingKPI3 = true;
    this.businessAnalyticsService()
      .retrieve('v1/business-analytics/sales/order/value-by-year', params)
      .then(res => {
        this.yearlyTotalData = res;
      })
      .catch(err => {
        this.yearlyTotalData = [];
        this.alertService().showHttpError(this, err.response);
      })
      .finally(() => {
        this.loadingKPI3 = false;
      });
  }

  /**
   * Fetch yearly fulfillment time data for KPI 4
   */
  getYearlyFulfillmentData() {
    const params = {
      ...this.queryParameters,
      year: this.currentYearKPI4.getFullYear(),
      currency: this.slide.data.currencyCode || 'EUR',
    };

    this.loadingKPI4 = true;
    this.businessAnalyticsService()
      .retrieve('v1/business-analytics/sales/order/fulfillment-time', params)
      .then(res => {
        this.yearlyFulfillmentData = res;
      })
      .catch(err => {
        this.yearlyFulfillmentData = [];
        this.alertService().showHttpError(this, err.response);
      })
      .finally(() => {
        this.loadingKPI4 = false;
      });
  }

  fetch() {
    this.getLineTotals();
    this.getMonthlyOrderValueData();
    this.getMonthlyOrderCountData();
    this.getYearlyTotalData();
    this.getYearlyFulfillmentData();
    this.getApexChart('v1/business-analytics/sales/order/lines/totals/by-reps', this.queryParameters, this.salesOrderTotalsByReps);
    this.getApexChart(
      'v1/business-analytics/sales/order/lines/totals/by-create-user',
      this.queryParameters,
      this.salesOrderTotalsByCreateUser
    );
    this.getApexChart('v1/business-analytics/sales/order/lines/totals/by-week', this.queryParameters, this.salesOrderTotalsByWeek);
    this.getApexChart('v1/business-analytics/sales/order/lines/totals/by-group', this.queryParameters, this.salesOrderTotalsByGroup);
  }

  public retrieveFunctionAuthorization(): Promise<boolean> {
    return new Promise(resolve => {
      this.isFunctionAuthorizationFetching = true;

      this.gardianService()
        .functionAuthorization({ function: ERP_FUNCTION })
        .then(param => {
          this.functionAuthorization.accessTypeObject = param.accessTypeObject;
          this.functionAuthorization.authorizationSite = param.authorizationSite;
          this.functionAuthorization.authorizedAccessTypeObject = param.authorizedAccessTypeObject;
          this.functionAuthorization.defaultSite = param.defaultSite;
          this.functionAuthorization.authorizedCompanies.push(...param.authorizedCompanies);
          this.functionAuthorization.authorizedSites.push(...param.authorizedSites);
          this.functionAuthorization.authorizedSalesRep.push(...param.authorizedSalesRep);
          this.isFunctionAuthorizationFetching = false;
          resolve(true);
        })
        .catch(error => {
          this.isFunctionAuthorizationFetching = false;
          const errMessage = error?.response?.data?.message ?? '';
          this.alertService().showAlert(this, AlertType.Error, `${error.message}${errMessage !== '' ? '<br>' + errMessage : ''}`, null, -1);
          resolve(false);
        });
    });
  }

  getLineTotals() {
    this.businessAnalyticsService()
      .retrieve('v1/business-analytics/sales/order/not-closed-lines/totals', this.queryParameters)
      .then(res => {
        this.slide.data.currencyCode = res['currency_code'];
        this.slide.data.netPriceExcludeTax = this.formatter
          .toLocaleInteger(res['open_orders_net_price_exclude_tax'])
          .replace(/[,\.]/g, ' ');
        this.slide.data.number = this.formatter.toLocaleInteger(res['open_orders_number']).replace(/[,\.]/g, ' ');
      })
      .catch(err => {
        if (err.response?.status === 404) {
          this.slide.data.currencyCode = '';
          this.slide.data.netPriceExcludeTax = 0;
          this.slide.data.number = 0;
          // this.alertService().showHttpError(this, err.response);
        }
      });
  }

  /**
   * Handler function for 'refresh-query'
   * @param payload
   */
  refreshQuery(payload) {
    // Update parent object to reflect parameters selections
    this.parameters = payload;
    this.queryParameters = this.parameterService().toQueryParameters(this.parameters);
    // Updates dashboard data
    this.fetch();
  }
}
